import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const labelOf = (m) => [m.title, m.case_number].filter(Boolean).join(" · ");

export const MatterCombobox = ({
  matters = [],
  value,
  onChange,
  testId = "matter-combobox",
  emptyLabel = "No matter",
  placeholder = "Type a case, number or client",
  height = "h-11",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selected = matters.find((m) => m.matter_id === value) || null;

  const pick = (id) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between rounded-sm ${height} text-base font-normal`}
        >
          <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>
            {selected ? labelOf(selected) : emptyLabel}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[min(92vw,26rem)]">
        <Command>
          <CommandInput
            data-testid={`${testId}-search`}
            placeholder={placeholder}
            className="text-base"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>Nothing matches that.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="none unlinked placeholder no matter"
                onSelect={() => pick(null)}
                data-testid={`${testId}-none`}
              >
                <Check className={`h-4 w-4 mr-2 shrink-0 ${value ? "opacity-0" : "opacity-100"}`} />
                {emptyLabel}
              </CommandItem>
              {matters.map((m) => (
                <CommandItem
                  key={m.matter_id}
                  value={`${m.title} ${m.case_number || ""} ${m.client_name || ""} ${m.matter_id}`}
                  onSelect={() => pick(m.matter_id)}
                  data-testid={`${testId}-option-${m.matter_id}`}
                >
                  <Check
                    className={`h-4 w-4 mr-2 shrink-0 ${
                      value === m.matter_id ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{m.title}</span>
                    {m.case_number || m.client_name ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {[m.case_number, m.client_name].filter(Boolean).join(" · ")}
                      </span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
